import { useState, useEffect } from 'react';
import * as api from '../services/api';

/**
 * エラーメッセージを取得
 */
const getErrorMessage = (error) => {
  if (!error) return '不明なエラーが発生しました';

  // ネットワークエラー
  if (error.message === 'Network Error' || !navigator.onLine) {
    return 'ネットワーク接続を確認してください';
  }

  // HTTPステータスコードによるエラー
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    switch (status) {
      case 401:
        return 'ログインが必要です';
      case 403:
        return 'アクセス権限がありません';
      case 404:
        return '情報が見つかりませんでした';
      case 409:
        return data?.message || '既に登録されています';
      case 429:
        return 'リクエストが多すぎます。しばらく待ってからお試しください';
      case 500:
        return 'サーバーエラーが発生しました。しばらく待ってからお試しください';
      default:
        return data?.message || `エラーが発生しました (${status})`;
    }
  }

  return error.message || 'エラーが発生しました';
};

/**
 * 課金情報を管理するカスタムフック
 */
export const useBilling = (userId) => {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isBuyingCredits, setIsBuyingCredits] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const fetchBillingData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await api.getBillingUsage(userId);
      setBillingData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching billing data:', err);
      const errorMessage = getErrorMessage(err);
      setError({ message: errorMessage, original: err });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, [userId]);

  const subscribe = async (plan) => {
    // 既存サブスクリプションチェック
    if (billingData?.subscription?.status === 'active' ||
        billingData?.subscription?.status === 'trialing') {
      alert('既にプランに加入しています。プラン変更は各プランの「このプランに変更」ボタンから行ってください。');
      return;
    }

    try {
      setIsSubscribing(true);
      const url = await api.createSubscriptionSession(userId, plan);
      // Stripeへリダイレクト（ローディング状態は維持）
      window.location.href = url;
    } catch (err) {
      console.error('Error subscribing:', err);
      const errorMessage = getErrorMessage(err);

      // ALREADY_SUBSCRIBEDエラーの特別処理
      if (err.response?.data?.code === 'ALREADY_SUBSCRIBED' ||
          err.message?.includes('ALREADY_SUBSCRIBED')) {
        alert('既にプランに加入しています。プラン変更は各プランの「このプランに変更」ボタンから行ってください。');
      } else {
        alert(`プラン購入に失敗しました\n\n${errorMessage}`);
      }
      setIsSubscribing(false);
    }
  };

  const buyCredits = async () => {
    try {
      setIsBuyingCredits(true);
      const url = await api.createTopupSession(userId);
      window.location.href = url;
    } catch (err) {
      console.error('Error buying credits:', err);
      const errorMessage = getErrorMessage(err);

      // TRIAL_PLAN_RESTRICTIONエラーの特別処理
      if (err.response?.data?.code === 'TRIAL_PLAN_RESTRICTION' ||
          err.message?.includes('TRIAL_PLAN_RESTRICTION')) {
        alert('追加クレジットは有料プラン（ライト・クリエイター）にアップグレード後にご購入いただけます。\n\nまずはプランをアップグレードしてください。');
      } else {
        alert(`クレジット購入に失敗しました\n\n${errorMessage}`);
      }
      setIsBuyingCredits(false);
    }
  };

  const manageSubscription = async () => {
    try {
      setIsOpeningPortal(true);
      const url = await api.createCustomerPortalSession(userId);
      window.location.href = url;
    } catch (err) {
      console.error('Error opening customer portal:', err);
      const errorMessage = getErrorMessage(err);

      // Customer Portal特有のエラー
      if (err.response?.status === 404) {
        alert('サブスクリプション情報が見つかりませんでした。\n\nプランに加入していない場合は、まずプランを選択してください。');
      } else {
        alert(`管理画面を開けませんでした\n\n${errorMessage}`);
      }
      setIsOpeningPortal(false);
    }
  };

  const changePlan = async (newPlan) => {
    if (!billingData) {
      alert('プラン情報を読み込み中です。しばらく待ってから再度お試しください。');
      return;
    }

    const currentPlan = billingData.plan;
    const planNames = {
      trial: 'トライアル',
      light: 'ライトプラン',
      creator: 'クリエイタープラン'
    };

    // 現在のプランと同じ場合
    if (currentPlan === newPlan) {
      alert('既にこのプランをご利用中です。');
      return;
    }

    // アップグレードかダウングレードかを判定
    const isUpgrade = (
      (currentPlan === 'trial' && ['light', 'creator'].includes(newPlan)) ||
      (currentPlan === 'light' && newPlan === 'creator')
    );

    // 確認ダイアログ
    const currentPeriodEnd = billingData.subscription?.currentPeriodEnd;
    const formattedDate = currentPeriodEnd
      ? new Date(currentPeriodEnd).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
      : '次回更新日';

    const message = isUpgrade
      ? `${planNames[newPlan]}にアップグレードしますか？\n\n` +
        `✅ 即座に適用されます\n` +
        `✅ 今期の使用回数がリセットされます\n` +
        `✅ 新しい請求サイクルが開始されます\n\n` +
        `今すぐ新プランの全機能がご利用いただけます。`
      : `${planNames[newPlan]}に変更しますか？\n\n` +
        `変更は${formattedDate}から適用されます。\n` +
        `それまでは現在のプランを引き続きご利用いただけます。`;

    if (!confirm(message)) {
      return;
    }

    try {
      setIsChangingPlan(true);
      const result = await api.changeSubscriptionPlan(userId, newPlan);

      if (result.immediate) {
        // アップグレード: 即時適用
        alert(
          `✅ ${planNames[newPlan]}にアップグレードしました！\n\n` +
          `即座に適用され、使用回数がリセットされました。\n` +
          `今すぐ新プランの全機能をご利用いただけます。`
        );
      } else {
        // ダウングレード: 次回更新日から適用
        const effectiveDate = new Date(result.effectiveDate).toLocaleDateString('ja-JP',
          { year: 'numeric', month: 'long', day: 'numeric' });
        alert(
          `プラン変更を予約しました！\n\n` +
          `${effectiveDate}から${planNames[newPlan]}に切り替わります。`
        );
      }

      // 最新の課金情報を再取得
      await fetchBillingData();
    } catch (err) {
      console.error('Error changing plan:', err);
      const errorMessage = getErrorMessage(err);
      alert(`プラン変更に失敗しました\n\n${errorMessage}`);
    } finally {
      setIsChangingPlan(false);
    }
  };

  return {
    billingData,
    loading,
    error,
    refetch: fetchBillingData,
    subscribe,
    buyCredits,
    manageSubscription,
    changePlan,
    // ローディング状態
    isSubscribing,
    isBuyingCredits,
    isChangingPlan,
    isOpeningPortal
  };
};
